import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Lock, CheckCircle, XCircle } from "lucide-react";

export default function FinalTest() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const [test, setTest] = useState(null);
  const [course, setCourse] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/api/courses/${courseId}`).then(({ data }) => setCourse(data));
    api.get(`/api/student/final-test/${courseId}`).then(({ data }) => setTest(data)).catch(() => setTest({ can_take: false }));
  };

  useEffect(() => { load(); }, [courseId]);

  const submit = async () => {
    if (!test) return;
    if (Object.keys(answers).length < test.questions.length) {
      toast.error("Please answer all questions");
      return;
    }
    setBusy(true);
    try {
      const ordered = test.questions.map((_, i) => answers[i] ?? -1);
      const { data } = await api.post("/api/student/final-test/submit", { course_id: courseId, answers: ordered });
      setResult(data);
      if (data.passed && data.certificate_id) {
        toast.success("Course completed! Certificate ready.");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (!test || !course) return <div className="py-24 text-center">Loading...</div>;

  if (!test.can_take && !result) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="final-test-locked">
        <Lock className="w-12 h-12 mx-auto text-[#D92D20]" />
        <h1 className="font-display font-black text-3xl mt-3">Almost there!</h1>
        <p className="text-[#4B5563] mt-2">Complete all lessons and pass every module quiz to unlock the Final Test.</p>
        <Link to={`/learn/${course.id}`} className="btn-primary inline-block mt-6">Back to lessons</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="final-test-page">
      <Link to={`/learn/${course.id}`} className="text-sm text-[#D92D20] font-semibold">← Back to lessons</Link>
      <div className="label-eyebrow mt-3">Final assessment</div>
      <h1 className="font-display font-black text-3xl md:text-4xl mt-1">{test.title}</h1>
      <p className="text-sm text-[#4B5563] mt-2">Pass mark: {test.pass_mark}% · {test.questions.length} questions</p>

      {result && (
        <div className={`mt-6 rounded-xl p-6 ${result.passed ? "bg-[#064E3B] text-white" : "bg-[#FEE2E2] border border-[#FCA5A5]"}`} data-testid="final-result">
          <div className="label-eyebrow text-white/80">{result.passed ? "Congratulations!" : "Almost there"}</div>
          <div className="font-display font-black text-5xl mt-1">{result.score}%</div>
          <p className="mt-2">{result.passed ? "You've completed the course." : `You need ${result.pass_mark}% to pass.`}</p>
          <div className="mt-4 flex gap-2">
            {result.passed && result.certificate_id && (
              <Link to={`/certificate/${result.certificate_id}`} data-testid="goto-certificate" className="btn-primary flex items-center gap-1">
                <Trophy className="w-4 h-4" /> View certificate
              </Link>
            )}
            {!result.passed && (
              <button onClick={() => { setAnswers({}); setResult(null); }} className="btn-primary" data-testid="final-retake">Retake</button>
            )}
            <Link to={`/learn/${course.id}`} className="btn-outline">Back to lessons</Link>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-6">
        {test.questions.map((q, qi) => {
          const r = result?.results?.[qi];
          const qKey = q.id || `q-${qi}-${(q.question || "").slice(0, 32)}`;
          return (
            <div key={qKey} data-testid={`fq-${qi}`} className="bg-white border border-[#E5E7EB] rounded-xl p-5">
              <div className="font-display font-bold">Q{qi+1}. {q.question}</div>
              <div className="mt-3 space-y-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[qi] === oi;
                  let cls = "border-[#E5E7EB] hover:border-[#D92D20]";
                  if (result && r) {
                    if (oi === r.correct_index) cls = "border-[#064E3B] bg-[#064E3B]/5";
                    else if (oi === r.selected && !r.is_correct) cls = "border-[#DC2626] bg-[#FEE2E2]";
                  } else if (selected) cls = "border-[#D92D20] bg-[#FECACA]/30";
                  return (
                    <button
                      key={`${qKey}-${opt}`}
                      data-testid={`fq${qi}-opt-${oi}`}
                      onClick={() => !result && setAnswers((a) => ({ ...a, [qi]: oi }))}
                      disabled={!!result}
                      className={`block w-full text-left border rounded-md px-3 py-2 text-sm ${cls}`}
                    >
                      <span className="font-semibold mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                      {result && oi === r.correct_index && <CheckCircle className="w-4 h-4 text-[#064E3B] inline ml-2" />}
                      {result && oi === r.selected && !r.is_correct && <XCircle className="w-4 h-4 text-[#DC2626] inline ml-2" />}
                    </button>
                  );
                })}
              </div>
              {result && r?.explanation && (
                <div className="mt-3 text-sm bg-[#FAFAFA] border border-[#E5E7EB] rounded-md p-3">
                  <span className="label-eyebrow">Explanation</span>
                  <p className="mt-1 text-[#4B5563]">{r.explanation}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!result && (
        <div className="mt-8 flex justify-end">
          <button onClick={submit} disabled={busy} data-testid="final-submit" className="btn-primary">Submit Final Test</button>
        </div>
      )}
    </div>
  );
}
